import React, {useEffect, useRef, useState} from 'react';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Label from '@mui/material/FormLabel';
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from '@mui/material/Stack';
import TextField, {TextFieldProps} from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ArrowForward from '@mui/icons-material/ArrowForward';

import {DateField} from '@mui/x-date-pickers/DateField';

import api from "../../services/api";
import {SectionProps} from '../../types/form';
import {Nationality} from "../../types/locations/nationality";

// @ts-ignore
import InputMask from 'react-input-mask';


const Section7 = (props: SectionProps) => {

    const {handleNext, values, setValues} = props;

    /**
     * LOAD NATIONALITIES
     */
    const [nationalities, setNationalities] = useState([]);

    useEffect(() => {
        api.get('locations/nationalities/search/').then(res => setNationalities(res.data.results));
    }, []);


    /**
     * FORM SUBMIT TO NEXT SECTION
     */
    const formRef = useRef<HTMLFormElement>(null);
    const [cpfError, setCpfError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [bDateError, setBDateError] = useState<string | null>(null);
    const [phone1Error, setPhone1Error] = useState(false);
    const [phone2Error, setPhone2Error] = useState(false);
    const [exEmployeeStartDateError, setExEmployeeStartDateError] = useState<string | null>(null);
    const [exEmployeeEndDateError, setExEmployeeEndDateError] = useState<string | null>(null);

    const handleNextClick = () => {
        if (!formRef.current) {
            return;
        }

        formRef.current.reportValidity();

        // Validate CPF
        if (values['cpf'].length !== 14) {
            setCpfError(true);
            return;
        }

        // Validate E-mail
        if (values['email'] && !values['email'].match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)) {
            setEmailError(true);
            return;
        }

        // Validate birth date formats and logic
        if (!values['birth_date'] || isNaN((new Date(values.birth_date?.toString())).valueOf())) {
            setBDateError('Data inválida');
            return;
        }

        if (values.birth_date > dayjs()) {
            setBDateError('Data de nascimento não pode ser maior que hoje');
            return;
        }

        // Validate ex-employee dates if applicable
        if (values.ex_employee) {
            const start = values.ex_employee_start_date;
            const end = values.ex_employee_end_date;

            // Validate dates format
            if (start && isNaN((new Date(start?.toString())).valueOf())) {
                setExEmployeeStartDateError('Data inválida');
                return;
            }

            if (end && isNaN((new Date(end?.toString())).valueOf())) {
                setExEmployeeEndDateError('Data inválida');
                return;
            }

            // Validate dates logic
            if (start && start > dayjs()) {
                setExEmployeeStartDateError('Data de início não pode ser maior que hoje');
                return;
            }

            if (end && end > dayjs()) {
                setExEmployeeEndDateError('Data de saída não pode ser maior que hoje');
                return;
            }

            if (start && end && start > end) {
                setExEmployeeEndDateError('Data de saída não pode ser menor que data de início');
                return;
            }
        }

        // Validate phone formats
        if (values['phone1'].length < 15) {
            setPhone1Error(true);
            return;
        }
        if (values['phone2'] && values['phone2'].length < 15) {
            setPhone2Error(true);
            return;
        }

        if (formRef.current.checkValidity()) {
            handleNext();
        }
    };

    /**
     * RENDER COMPONENT
     */
    return (
        <Box>
            <Typography variant={'h2'}>
                Dados Pessoais
            </Typography>

            <Box component={'form'} ref={formRef}>

                <Grid container spacing={3}>

                    <Grid item sm={6}>
                        <FormControl fullWidth>
                            <Label sx={{mb: 1}}>Nacionalidade *</Label>
                            <Select
                                required={true}
                                value={values.nationality ?? ''}
                                onChange={(e) => setValues({
                                    ...values,
                                    nationality: e.target.value
                                })}
                            >
                                {nationalities.map((n: Nationality) => (
                                    <MenuItem key={n.id} value={n.id}
                                              children={n.description}/>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item sm={6}>
                        <FormControl fullWidth>
                            <Label sx={{mb: 1}}>CPF *</Label>
                            <InputMask
                                mask={'999.999.999-99'}
                                maskChar={null}
                                value={values['cpf']}
                                onChange={(e: any) => {
                                    setCpfError(false);
                                    setValues({...values, cpf: e.target.value});
                                }}
                            >
                                {(inputProps: TextFieldProps) =>
                                    <TextField
                                        {...inputProps}
                                        required
                                        fullWidth
                                        error={cpfError}
                                        helperText={cpfError ? 'CPF inválido.' : ''}
                                    />
                                }
                            </InputMask>
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControl fullWidth>
                            <Label sx={{mb: 1}}>Nome Completo *</Label>
                            <TextField
                                required
                                fullWidth
                                variant={'outlined'}
                                value={values['full_name']}
                                onChange={e => setValues({
                                    ...values,
                                    full_name: e.target.value
                                })}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControl fullWidth>
                            <Label sx={{mb: 1}}>
                                E-mail (Digite seu e-mail para receber o retorno
                                da nossa equipe de seleção)
                            </Label>
                            <TextField
                                fullWidth
                                variant={'outlined'}
                                value={values['email']}
                                onChange={e => setValues({
                                    ...values,
                                    email: e.target.value
                                })}
                                error={emailError}
                                helperText={emailError ? 'E-mail inválido.' : ''}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item sm={6}>
                        <FormControl fullWidth>
                            <Label sx={{mb: 1}}>Data de nascimento *</Label>
                            <DateField
                                value={values['birth_date']}
                                onChange={newValue => setValues({
                                    ...values,
                                    birth_date: newValue
                                })}
                                slotProps={{
                                    textField: {
                                        error: !!bDateError,
                                        helperText: bDateError ?? '',
                                    },
                                }}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item sm={6}></Grid>

                    <Grid item sm={6}>
                        <FormControl fullWidth>
                            <Label sx={{mb: 1}}>WhatsApp *</Label>
                            <InputMask
                                mask={'(99) 9 9999-9999'}
                                maskChar={null}
                                value={values['phone1']}
                                onChange={(e: any) => setValues({
                                    ...values,
                                    phone1: e.target.value
                                })}
                            >
                                {(inputProps: TextFieldProps) =>
                                    <TextField
                                        {...inputProps}
                                        required
                                        fullWidth
                                        error={phone1Error}
                                        helperText={phone1Error ? 'Telefone inválido.' : ''}
                                    />
                                }
                            </InputMask>
                        </FormControl>
                    </Grid>

                    <Grid item sm={6}>
                        <FormControl fullWidth>
                            <Label sx={{mb: 1}}>Telefone alternativo</Label>
                            <InputMask
                                mask={'(99) 9 9999-9999'}
                                maskChar={null}
                                value={values['phone2']}
                                onChange={(e: any) => setValues({
                                    ...values,
                                    phone2: e.target.value
                                })}
                            >
                                {(inputProps: TextFieldProps) =>
                                    <TextField
                                        {...inputProps}
                                        fullWidth
                                        error={phone2Error}
                                        helperText={phone2Error ? 'Telefone inválido.' : ''}
                                    />
                                }
                            </InputMask>
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControl fullWidth>
                            <FormControlLabel
                                control={<Checkbox
                                    size={'small'}
                                    sx={{py: 0.3}}
                                    checked={values['ex_employee']}
                                    onChange={(e) => setValues({
                                        ...values,
                                        ex_employee: e.target.checked
                                    })}
                                />}
                                label={'Ex-funcionário da Marvel'}
                            />
                        </FormControl>
                    </Grid>

                    {values.ex_employee && <>
                        <Grid item sm={6}>
                            <FormControl fullWidth>
                                <Label sx={{mb: 1}} children={'Data de início'}/>
                                <DateField
                                    value={values.ex_employee_start_date}
                                    onChange={newValue => setValues({
                                        ...values,
                                        ex_employee_start_date: newValue
                                    })}
                                    slotProps={{
                                        textField: {
                                            error: !!exEmployeeStartDateError,
                                            helperText: exEmployeeStartDateError ?? '',
                                        },
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={6}>
                            <FormControl fullWidth>
                                <Label sx={{mb: 1}} children={'Data de saída'}/>
                                <DateField
                                    value={values.ex_employee_end_date}
                                    onChange={newValue => setValues({
                                        ...values,
                                        ex_employee_end_date: newValue
                                    })}
                                    slotProps={{
                                        textField: {
                                            error: !!exEmployeeEndDateError,
                                            helperText: exEmployeeEndDateError ?? '',
                                        },
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item sm={12}>
                            <FormControl fullWidth>
                                <Label sx={{mb: 1}}
                                       children={'Motivo do desligamento *'}/>
                                <TextField
                                    multiline
                                    fullWidth
                                    required
                                    variant={'outlined'}
                                    value={values.ex_employee_resignation_reason}
                                    onChange={e => setValues({
                                        ...values,
                                        ex_employee_resignation_reason: e.target.value
                                    })}
                                />
                            </FormControl>
                        </Grid>
                    </>}
                </Grid>

                <Stack direction={'row'} justifyContent={'end'}
                       sx={{mt: 5}}>
                    <Button variant={'contained'} onClick={handleNextClick}>
                        Próximo
                        <ArrowForward fontSize={'small'} sx={{ml: 2}}/>
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
};

export default Section7;
